/* eslint-disable */
// @ts-nocheck

import * as React from 'react';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import InlineMessage from '@atlaskit/inline-message';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';

const DISMISS_STORAGE_KEY_PREFIX = 'connect-eos-banner-dismissed';

const FEATURE_GATE_BANNER_ENABLED = 'customer-messaging-iframe-banner-enabled';

const LEARN_MORE_URL: string =
	'https://www.atlassian.com/blog/developer/announcing-connect-end-of-support-timeline-and-next-steps';

interface ConnectEosBannerProps {
	appKey: string;
	isSystemApp?: boolean;
	moduleKey: string;
	moduleType?: string;
}

interface ConnectEosBannerState {
	dismissed: boolean;
}

const containerStyles: React.CSSProperties = {
	marginBottom: '8px',
};

function getDismissStorageKey(appKey: string): string {
	return `${DISMISS_STORAGE_KEY_PREFIX}-${appKey}`;
}

class ConnectEosBanner extends React.PureComponent<ConnectEosBannerProps, ConnectEosBannerState> {
	static defaultProps = {
		isSystemApp: false,
		moduleType: '',
	};

	constructor(props: ConnectEosBannerProps) {
		super(props);
		this.state = {
			dismissed: this._isDismissed(),
		};
	}

	_isDismissed(): boolean {
		try {
			return localStorage.getItem(getDismissStorageKey(this.props.appKey)) === 'true';
		} catch {
			return false;
		}
	}

	_dismiss = (): void => {
		try {
			localStorage.setItem(getDismissStorageKey(this.props.appKey), 'true');
		} catch {
			// localStorage may be unavailable
		}
		this.setState({ dismissed: true });
	};

	_shouldShowBanner(): boolean {
		// Don't show for system apps
		if (this.props.isSystemApp) {
			return false;
		}

		// Don't show if the main feature gate is not enabled
		if (!FeatureGates.checkGate(FEATURE_GATE_BANNER_ENABLED)) {
			return false;
		}

		// Don't show if previously dismissed (within configured dismiss duration)
		if (this.state.dismissed) {
			return false;
		}

		return true;
	}

	getSectionWarningBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-banner" style={containerStyles}>
				<SectionMessage
					appearance="warning"
					title="This app might stop working soon"
					actions={[
						<SectionMessageAction onClick={this._dismiss} key="dismiss">
							Dismiss
						</SectionMessageAction>,
						<SectionMessageAction href={LEARN_MORE_URL} key="learn-more">
							Learn more
						</SectionMessageAction>,
					]}
				>
					<p>
						{this.props.appKey || '[appKey missing]'} might stop working in December 2026, as it's
						built on a legacy platform that'll become unsupported on that date. To explore your
						options, contact your site admin.
					</p>
				</SectionMessage>
			</div>
		);
	}

	getInlineBanner(): React.ReactElement<any> | null {
		return (
			<div data-testid="connect-eos-v2-banner">
				<InlineMessage appearance="warning" secondaryText="This app might stop working soon">
					<p>
						{this.props.appKey || '[appKey missing]'} might stop working in December 2026, as it’s
						built on a legacy platform that’ll become unsupported on that date. To explore your
						options, contact your site admin.
					</p>

					<p>
						<SectionMessageAction onClick={this._dismiss} key="dismiss">
							Dismiss
						</SectionMessageAction>
						&nbsp;·&nbsp;
						<SectionMessageAction href={LEARN_MORE_URL} key="learn-more">
							Learn more
						</SectionMessageAction>
					</p>
				</InlineMessage>
			</div>
		);
	}

	render(): React.ReactElement<any> | null {
		if (!this._shouldShowBanner()) {
			return null;
		}

		if (this.props.moduleType === 'webPanels') {
			return this.getInlineBanner();
		} else {
			return this.getSectionWarningBanner();
		}
	}
}

export {
	ConnectEosBanner,
	DISMISS_STORAGE_KEY_PREFIX,
	FEATURE_GATE_BANNER_ENABLED,
	LEARN_MORE_URL,
	getDismissStorageKey,
};
